import type { AutoBlockConfig } from '@yawi/types';
import { AlertTriangle, Loader2, Shield, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

// El bloqueo por cobranza es TOTAL y no es configurable: solo sobreviven
// consultar y pagar adeudos (ver BLACKLIST_ALLOWED_PATHS en el backend).
const BLOCK_EFFECTS = [
  'Acceso personal del residente (QR, PIN y entrada en auto)',
  'Invitaciones, reservas de amenidades y apertura de puertas',
  'Comunicados, encuestas, documentos y proyectos',
  'Reportes, mascotas, vehículos y placas',
];

interface AutoBlockModalProps {
  open: boolean;
  loading: boolean;
  saving: boolean;
  config: AutoBlockConfig | null;
  onClose: () => void;
  onSave: (config: AutoBlockConfig) => Promise<boolean>;
}

export const AutoBlockModal: React.FC<AutoBlockModalProps> = ({
  open,
  loading,
  saving,
  config,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState<AutoBlockConfig | null>(config);

  useEffect(() => {
    if (open) setDraft(config);
  }, [open, config]);

  if (!open) return null;

  const handleSave = async () => {
    if (!draft) return;
    const ok = await onSave(draft);
    if (ok) onClose();
  };

  const update = (patch: Partial<AutoBlockConfig>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-3xl bg-white shadow-xl">
        <div className="flex items-start gap-4 border-b border-slate-100 p-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-[#7C5CFC]">
            <Shield size={24} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-800">Bloqueo automático</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Define cuándo el sistema bloquea solo a los morosos
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="-mr-1 -mt-1 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={20} />
          </button>
        </div>

        {loading || !draft ? (
          <div className="space-y-4 p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : (
          <>
            <div className="space-y-4 overflow-y-auto px-6 py-5">
              <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 p-3.5">
                <div>
                  <p className="text-sm font-bold text-slate-800">Activar bloqueo automático</p>
                  <p className="mt-0.5 text-sm text-slate-500">
                    Si lo apagas, el bloqueo será solo manual
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={draft.enabled}
                  aria-label="Activar bloqueo automático"
                  onClick={() => update({ enabled: !draft.enabled })}
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
                    draft.enabled ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      draft.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <fieldset disabled={!draft.enabled} className="space-y-4 disabled:opacity-50">
                <div>
                  <p className="mb-2 text-sm font-bold text-slate-800">
                    Bloquear cuando el residente acumule
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {([1, 2, 3] as const).map((months) => {
                      const active = draft.monthsThreshold === months;
                      return (
                        <button
                          key={months}
                          type="button"
                          onClick={() => update({ monthsThreshold: months })}
                          className={`flex cursor-pointer flex-col items-center gap-0.5 rounded-xl border-2 py-3.5 transition-colors ${
                            active
                              ? 'border-[#7C5CFC] bg-violet-50'
                              : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}
                        >
                          <span
                            className={`text-xl font-bold ${active ? 'text-[#7C5CFC]' : 'text-slate-700'}`}
                          >
                            {months}
                          </span>
                          <span className={`text-xs ${active ? 'text-[#7C5CFC]' : 'text-slate-500'}`}>
                            {months === 1 ? 'mes' : 'meses'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label htmlFor="cobranza-day" className="mb-2 block text-sm font-bold text-slate-800">
                    Aplicar el bloqueo el día
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      id="cobranza-day"
                      type="number"
                      min={1}
                      max={28}
                      value={draft.dayOfMonth}
                      onChange={(e) => {
                        const value = Math.min(28, Math.max(1, Number(e.target.value) || 1));
                        update({ dayOfMonth: value });
                      }}
                      className="w-20 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium focus:border-[#7C5CFC] focus:outline-none focus:ring-1 focus:ring-[#7C5CFC]"
                    />
                    <span className="text-sm text-slate-500">de cada mes</span>
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-sm font-bold text-slate-800">Qué se restringe al bloquear</p>
                  <ul className="space-y-1.5">
                    {BLOCK_EFFECTS.map((label) => (
                      <li key={label} className="flex items-center gap-2 text-sm text-slate-600">
                        <X size={16} className="shrink-0 text-red-500" />
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>
              </fieldset>

              <div className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-700">
                <AlertTriangle size={18} className="mt-0.5 shrink-0" strokeWidth={2.5} />
                <p>
                  El bloqueo es <strong>total</strong>: el residente solo podrá consultar y pagar sus
                  adeudos. Su acceso personal (QR, PIN peatonal y entrada en auto) también queda
                  bloqueado hasta que regularice.
                </p>
              </div>
            </div>

            <div className="flex gap-3 border-t border-slate-100 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 cursor-pointer rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex flex-[1.6] cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#7C5CFC] px-4 py-3 text-sm font-semibold text-white hover:bg-[#6b4ce0] disabled:opacity-60"
              >
                {saving && <Loader2 size={18} className="animate-spin" />}
                Guardar configuración
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
